import { Injectable } from "@angular/core";
import { DbDaoBase } from "../../../../../gyzmo-commons/dao/db/base/db.dao.base";
import { NotImplementedError } from "../../../../../gyzmo-commons/helpers/notImplementedError";
import { AppSqlProvider } from "../../../../../gyzmo-commons/persistence/app.sql.provider";
import { LoggerService } from "../../../../../gyzmo-commons/services/logs/logger.service";
import { ZoneElement } from "../../../../models/inspection/v2/zoneElement.model";
import { DamageCategoryDbDao } from "../common/damageCategory.db.dao";

@Injectable({
    providedIn: "root",
})
export class ZoneElementDbDaoV2 extends DbDaoBase<ZoneElement> {
    constructor(
        logger: LoggerService,
        private sqlProvider: AppSqlProvider,
        private damageCategoryDbDao: DamageCategoryDbDao,
    ) {
        super(logger);
    }

    getByZoneId(zoneId: string, hydrate: boolean = true): Promise<ZoneElement[]> {
        const selectQuery = `SELECT * FROM ${ ZoneElement.TABLENAME } WHERE parentZoneId = ?;`;
        return this.sqlProvider.query(selectQuery, [zoneId])
            .then(async data => {
                if (data.rows.length <= 0) {
                    return [];
                }
                const zoneElements: ZoneElement[] = [];
                for (const item of data.rows) {
                    const zoneElement = this.rowToModel(item);
                    zoneElements.push(zoneElement);
                }
                const hydratationPromises = [];
                if (hydrate) {
                    zoneElements.forEach(zoneElement => {
                        hydratationPromises.push(this.damageCategoryDbDao.get(zoneElement.damageCategory.id, hydrate)
                            .then(value => {
                                zoneElement.damageCategory = value;
                            }));
                    });
                }
                return Promise.all(hydratationPromises)
                    .then(() => zoneElements);
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public getList(hydrate: boolean = true): Promise<ZoneElement[]> {
        const selectQuery = `SELECT * FROM ${ ZoneElement.TABLENAME };`;
        return this.sqlProvider.query(selectQuery, [])
            .then(data => {
                if (data.rows.length <= 0) {
                    return [];
                }
                const zoneElements: ZoneElement[] = [];
                for (const item of data.rows) {
                    const zoneElement = this.rowToModel(item);
                    zoneElements.push(zoneElement);
                }
                const hydratationPromises = [];
                if (hydrate) {
                    zoneElements.forEach(zoneElement => {
                        hydratationPromises.push(this.damageCategoryDbDao.get(zoneElement.damageCategory.id, hydrate)
                            .then(value => {
                                zoneElement.damageCategory = value;
                            }));
                    });
                }
                return Promise.all(hydratationPromises)
                    .then(() => zoneElements);
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    public async createIndexes(): Promise<void> {
        let query = `CREATE INDEX IF NOT EXISTS idx_${ ZoneElement.TABLENAME }_id ON ${ ZoneElement.TABLENAME }(id);`;
        await this.sqlProvider.query(query, [])
            .catch(reason => {
                this.logSqlError(reason);
            });
        query = `CREATE INDEX IF NOT EXISTS idx_${ ZoneElement.TABLENAME }_parentZoneId ON ${ ZoneElement.TABLENAME }(parentZoneId);`;
        await this.sqlProvider.query(query, [])
            .catch(reason => {
                this.logSqlError(reason);
            });
    }

    public createTable(): Promise<void> {
        const query = `CREATE TABLE IF NOT EXISTS ${ ZoneElement.TABLENAME } (
            key TEXT PRIMARY KEY,
            id TEXT,
            parentZoneId TEXT,
            wording TEXT,
            filter TEXT,
            filter2 TEXT,
            damageCategoryId TEXT,
            _order TEXT
        );`;
        return this.sqlProvider.query(query, [])
            .then(async () => {
                await this.createIndexes();
            })
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }

    delete(id: string): Promise<any> {
        const deleteQuery = `DELETE FROM ${ ZoneElement.TABLENAME } WHERE id = ?;`;
        return this.sqlProvider.query(deleteQuery, [id]);
    }

    deleteAll(): Promise<any> {
        const deleteQuery = `DELETE FROM ${ ZoneElement.TABLENAME };`;
        return this.sqlProvider.query(deleteQuery, []);
    }

    public get(id: string, hydrate: boolean = false): Promise<ZoneElement> {
        throw new NotImplementedError();
    }

    public getTableName(): string {
        return ZoneElement.TABLENAME;
    }

    protected rowToModel(row: any): ZoneElement {
        const zoneElement = new ZoneElement();
        zoneElement.id = row.id;
        zoneElement.parentZoneId = row.parentZoneId;
        zoneElement.wording = row.wording;
        zoneElement.filter = row.filter;
        zoneElement.filter2 = row.filter2;
        zoneElement.order = row._order;
        zoneElement.damageCategory.id = row.damageCategoryId;
        return zoneElement;
    }

    public save(zoneElement: ZoneElement): Promise<ZoneElement> {
        const query = `INSERT OR REPLACE INTO ${ ZoneElement.TABLENAME }
            (key, id, parentZoneId, wording, filter, filter2, damageCategoryId, _order)
            VALUES (?, ?, ?, ?, ?, ?, ?, ?);`;
        const params = [
            `${ zoneElement.id }_${ zoneElement.parentZoneId }`,
            zoneElement.id,
            zoneElement.parentZoneId,
            zoneElement.wording,
            zoneElement.filter,
            zoneElement.filter2,
            zoneElement.damageCategory ? zoneElement.damageCategory.id : null,
            zoneElement.order,
        ];
        return this.sqlProvider.query(query, params)
            .then(() => zoneElement)
            .catch(reason => {
                this.logSqlError(reason);
                return null;
            });
    }
}
